import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message as antdMessage } from "antd";
import { chatService } from "src/shared/services/chat";
import { signalRHub } from "src/shared/services/chat/signalr-hub";
import {
  setRooms,
  setMessages,
  addMessage,
  setCurrentRoomId,
  setIsLoadingRooms,
  setIsLoadingMessages,
  setIsSendingMessage,
  removeMessage,
  markRoomAsRead,
  prependMessages,
  addToOfflineQueue,
  removeFromOfflineQueue,
  setIsOnline,
  setSearchResults,
  setIsSearching,
} from "src/domain/app/redux/chat/chat-slice";
import {
  selectAllRooms,
  selectCurrentRoomId,
  selectMessagesForRoom,
  selectIsLoadingRooms,
  selectIsLoadingMessages,
  selectIsSendingMessage,
  selectOfflineQueue,
  selectIsOnline,
} from "src/domain/app/redux/chat/chat-selectors";
import { SendMessageDto } from "src/shared/services/chat/dto";
import { setupGlobalChatListeners } from "src/shared/services/chat/realtime";

export const useChat = () => {
  const dispatch = useDispatch();
  const rooms = useSelector(selectAllRooms);
  const currentRoomId = useSelector(selectCurrentRoomId);
  const isLoadingRooms = useSelector(selectIsLoadingRooms);
  const isLoadingMessages = useSelector(selectIsLoadingMessages);
  const isSendingMessage = useSelector(selectIsSendingMessage);
  const offlineQueue = useSelector(selectOfflineQueue);
  const isOnline = useSelector(selectIsOnline);
  const currentMessages = useSelector(
    selectMessagesForRoom(currentRoomId || ""),
  );

  const hasInitialized = useRef(false);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      dispatch(setIsOnline(true));
      processOfflineQueue();
    };

    const handleOffline = () => {
      dispatch(setIsOnline(false));
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [offlineQueue]);

  const initializeChat = useCallback(async () => {
    try {
      // The SignalR connection and its message listeners are set up globally in
      // AuthProvider (see services/chat/realtime) so the unread badge updates
      // without the chat being open — registering them again here would count
      // every message twice. Only the room list needs refreshing on mount.
      setupGlobalChatListeners();
      await loadRooms();
    } catch (error) {
      console.error("Failed to initialize chat:", error);
      antdMessage.error("Failed to initialize chat");
    }
  }, []);

  // Load rooms on mount; realtime listeners are global and outlive this hook
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      initializeChat();
    }
  }, [initializeChat]);

  const requestNotificationPermission = async () => {
    if ("Notification" in window && Notification.permission === "default") {
      await Notification.requestPermission();
    }
  };

  const loadRooms = async () => {
    try {
      dispatch(setIsLoadingRooms(true));
      const response = await chatService.getUserChatRooms();
      if (response?.Data) {
        dispatch(setRooms(response.Data.data));
      }
    } catch (error) {
      console.error("Failed to load rooms:", error);
      antdMessage.error("Failed to load chat rooms");
    } finally {
      dispatch(setIsLoadingRooms(false));
    }
  };

  const loadMessages = async (roomId: string, pageNumber: number = 1) => {
    try {
      dispatch(setIsLoadingMessages(true));
      const response = await chatService.getMessages({
        roomId,
        pageNumber,
        pageSize: 50,
      });

      if (response?.Data) {
        if (pageNumber === 1) {
          dispatch(setMessages({ roomId, messages: response.Data.data }));
        } else {
          dispatch(prependMessages({ roomId, messages: response.Data.data }));
        }
      }
    } catch (error) {
      console.error("Failed to load messages:", error);
      antdMessage.error("Failed to load messages");
    } finally {
      dispatch(setIsLoadingMessages(false));
    }
  };

  const joinRoom = async (roomId: string) => {
    try {
      if (currentRoomId) {
        await signalRHub.leaveRoom(currentRoomId);
      }

      dispatch(setCurrentRoomId(roomId));
      await signalRHub.joinRoom(roomId);
      await loadMessages(roomId);
      await markAsRead(roomId);
    } catch (error) {
      console.error("Failed to join room:", error);
      antdMessage.error("Failed to join chat room");
    }
  };

  const leaveRoom = async () => {
    try {
      if (currentRoomId) {
        await signalRHub.leaveRoom(currentRoomId);
        dispatch(setCurrentRoomId(null));
      }
    } catch (error) {
      console.error("Failed to leave room:", error);
    }
  };

  const sendMessage = async (input: SendMessageDto) => {
    if (!isOnline) {
      // Queue message for offline mode
      const tempId = `temp-${Date.now()}`;
      dispatch(
        addToOfflineQueue({
          ...input,
          tempId,
        }),
      );
      antdMessage.info("Message queued. Will send when online.");
      return;
    }

    try {
      dispatch(setIsSendingMessage(true));
      const response = await chatService.sendMessage({
        ...input,
      });

      if (response?.Data) {
        // Optimistically add the message to the UI immediately
        dispatch(
          addMessage({ roomId: input.chatRoomId, message: response.Data }),
        );
        return response.Data;
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      antdMessage.error("Failed to send message");
    } finally {
      dispatch(setIsSendingMessage(false));
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!currentRoomId) return;

    try {
      await chatService.deleteMessage(messageId);
      dispatch(removeMessage({ roomId: currentRoomId, messageId }));
      antdMessage.success("Message deleted");
    } catch (error) {
      console.error("Failed to delete message:", error);
      antdMessage.error("Failed to delete message");
    }
  };

  const markAsRead = async (roomId: string) => {
    try {
      await chatService.markRoomAsRead({ roomId });
      dispatch(markRoomAsRead(roomId));
    } catch (error) {
      console.error("Failed to mark room as read:", error);
    }
  };

  const searchMessages = async (roomId: string, searchText: string) => {
    try {
      dispatch(setIsSearching(true));
      const response = await chatService.searchMessages(roomId, searchText);

      if (response?.Data) {
        dispatch(setSearchResults(response.Data));
      }
    } catch (error) {
      console.error("Failed to search messages:", error);
      antdMessage.error("Failed to search messages");
    } finally {
      dispatch(setIsSearching(false));
    }
  };

  const processOfflineQueue = async () => {
    if (offlineQueue.length === 0) return;

    for (const queuedMessage of offlineQueue) {
      try {
        await chatService.sendMessage({
          chatRoomId: queuedMessage.chatRoomId,
          message: queuedMessage.message,
        });
        dispatch(removeFromOfflineQueue(queuedMessage.tempId));
      } catch (error) {
        console.error("Failed to send queued message:", error);
      }
    }

    if (offlineQueue.length > 0) {
      antdMessage.success("Offline messages sent");
    }
  };

  return {
    rooms,
    currentRoomId,
    currentMessages,
    isLoadingRooms,
    isLoadingMessages,
    isSendingMessage,
    isOnline,
    loadRooms,
    loadMessages,
    joinRoom,
    leaveRoom,
    sendMessage,
    deleteMessage,
    markAsRead,
    searchMessages,
    requestNotificationPermission,
  };
};
